import { BooleanInput } from '@angular/cdk/coercion';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
    provideTranslocoScope,
    TranslocoModule,
    TranslocoService,
} from '@jsverse/transloco';
import { AuthService } from 'app/core/auth/auth.service';
import { CognitoAuthService } from 'app/core/auth/cognito-auth.service';
import { UserService } from 'app/core/services/user.service';
import { TeamService } from 'app/modules/admin/team/team/team.service';
import { firstValueFrom, lastValueFrom, Subject, Subscription } from 'rxjs';
import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';
import { SharedService } from 'app/core/services/shared.service';
import { MyDataService } from 'app/modules/admin/team/my-data/my-data.service';
import { HeadlineService } from 'app/core/services/headline.service';
import { AutoLogoutService } from 'app/core/services/auto-logout.service';
import { SessionSyncService } from 'app/core/services/session-sync.service';
import { StaffPlanService } from 'app/modules/admin/events/pages/staff-plan/staff-plan.service';
@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatDividerModule,
        MatTooltipModule,
        TranslocoModule,
        FormsModule,
        TranslocoModule,
        MatCheckboxModule,
    ],
    providers: [
        provideTranslocoScope('codext'),
        provideTranslocoScope('p_usd'),
        provideTranslocoScope('group'),
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    environment = environment;
    userImagePath: string | null = null;
    pictureChangedSub: Subscription;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        public userService: UserService,
        public _authService: AuthService,
        public translocoService: TranslocoService,
        private httpClient: HttpClient,
        private sharedService: SharedService,
        private myDataService: MyDataService,
        public headlineService: HeadlineService,
        private autoLogoutService: AutoLogoutService,
        private staffplanService: StaffPlanService,
        private cognitoAuthService: CognitoAuthService,
        private sessionSyncService: SessionSyncService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit() {
        this.pictureChangedSub = this.sharedService.pictureChanged.subscribe(
            () => {
                this.loadPicture();
            }
        );
        await this.loadPicture();
        console.log(this.userImagePath);
    }

    async loadPicture() {
        try {
            let formData = new FormData();
            formData.append('usr_id', this.userService.user.id ?? '');
            formData.append('option', 'image_path');
            const data = await lastValueFrom(
                this.httpClient.post(
                    `hippo/ajax_navigator.php?page=pages/api/Ajax_user_data.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            let jsonData = JSON.parse(JSON.stringify(data));
            this.userImagePath = null;
            this._changeDetectorRef.detectChanges();
            setTimeout(() => {
                this.userImagePath = jsonData.body.result
                    ? jsonData.body.result + '?t=' + new Date().getTime()
                    : null;
                this._changeDetectorRef.detectChanges();
            }, 100);
        } catch (error) {
            this.userImagePath = null;
            console.log(error);
            this.sharedService.sendErrorMail(
                error,
                'UserComponent',
                'loadPicture'
            );
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        if (this.pictureChangedSub) {
            this.pictureChangedSub.unsubscribe();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.userService.user) {
            return;
        }

        // Update the user
        /*this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();*/
    }

    /**
     * Sign out
     */
    async signOut(): Promise<void> {
        // Tell the other tabs first — the Cognito path below leaves this tab
        // via a hosted-UI redirect, so anything after it may not run (HIP-40).
        this.sessionSyncService.broadcastLogout();
        this._authService.signOut().subscribe();
        this.autoLogoutService.clearTimer();
        this.staffplanService.resetFilter();

        // If the user signed in via AWS Cognito, clearing the local PHP session
        // alone leaves the Cognito/OIDC session intact — a refresh would then
        // re-exchange the still-valid token and log the user straight back in.
        // Route through the full Cognito logout (revokes the OIDC session and
        // redirects to the hosted-UI logout) so the sign-out actually sticks
        // (HIP-33).
        let cognitoSession: any = null;
        try {
            cognitoSession = await firstValueFrom(
                this.cognitoAuthService.isAuthenticated$
            );
        } catch {}
        if (cognitoSession?.isAuthenticated) {
            this.cognitoAuthService.logout();
            return;
        }

        this._router.navigate(['/sign-in']);
    }

    goToMyData() {
        this._router.navigate(['/team/my-data']);
        setTimeout(() => {
            this.myDataService.switchToGeneralTab.next();
        }, 100);
    }

    goToMyDataSettings() {
        this._router.navigate(['/team/my-data']);
        setTimeout(() => {
            this.sharedService.goToMyDataSettings.next();
        }, 100);
    }
}
